import { useMutation } from '@tanstack/react-query';
import { getCarbonArcConnectUrl } from '@/services/integrations/carbon-arc';
import { CARBON_ARC_PROVIDER } from '@/utils/oauth-connect-signal';

type ConnectUrlMinter = (
  orgId: string,
  returnTo?: string,
) => Promise<string | null>;

// Mirrors PROVIDER_BY_NODE_SUB_TYPE on the pre-run check: an unrecognised slug
// fails loudly rather than minting some other provider's URL.
const MINTERS: Record<string, ConnectUrlMinter> = {
  [CARBON_ARC_PROVIDER]: getCarbonArcConnectUrl,
};

interface StartOAuthConnectInput {
  provider: string;
  orgId: string;
  returnTo?: string;
}

const startOAuthConnect = async ({
  provider,
  orgId,
  returnTo,
}: StartOAuthConnectInput): Promise<string> => {
  const mint = MINTERS[provider];
  if (!mint) {
    throw new Error(`No connect flow is configured for ${provider}`);
  }

  const authorizeUrl = await mint(orgId, returnTo);
  if (!authorizeUrl) {
    throw new Error('No authorize URL returned');
  }

  return authorizeUrl;
};

export const useStartOAuthConnect = () =>
  // No retry: the app default retries mutations once, and every attempt writes
  // a fresh single-use PKCE state server-side. A failed mint would leave a
  // second one behind that nothing ever consumes.
  useMutation({ mutationFn: startOAuthConnect, retry: false });
